import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ChevronUp, ChevronDown, Plane, Loader2, Save } from 'lucide-react';
import { vehicleCategoriesAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const AdminVehicleCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ id: '', name: '', name_tr: '' });
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await vehicleCategoriesAPI.getAll();
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      toast.error(language === 'tr' ? 'Kategoriler yüklenemedi' : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.id || !formData.name) {
      toast.error(language === 'tr' ? 'ID ve İngilizce isim zorunludur' : 'ID and English name are required');
      return;
    }

    try {
      await vehicleCategoriesAPI.create(formData);
      toast.success(language === 'tr' ? 'Kategori oluşturuldu' : 'Category created');
      setIsCreating(false);
      setFormData({ id: '', name: '', name_tr: '' });
      fetchCategories();
    } catch (err) {
      toast.error(err.message || (language === 'tr' ? 'Kategori oluşturulamadı' : 'Failed to create category'));
    }
  };

  const handleUpdate = async (categoryId) => {
    try {
      await vehicleCategoriesAPI.update(categoryId, formData);
      toast.success(language === 'tr' ? 'Kategori güncellendi' : 'Category updated');
      setEditingId(null);
      setFormData({ id: '', name: '', name_tr: '' });
      fetchCategories();
    } catch (err) {
      toast.error(err.message || (language === 'tr' ? 'Kategori güncellenemedi' : 'Failed to update category'));
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm(language === 'tr' 
      ? 'Bu kategoriyi silmek istediğinizden emin misiniz?' 
      : 'Are you sure you want to delete this category?'
    )) return;

    try {
      await vehicleCategoriesAPI.delete(categoryId);
      toast.success(language === 'tr' ? 'Kategori silindi' : 'Category deleted');
      fetchCategories();
    } catch (err) {
      toast.error(err.message || (language === 'tr' ? 'Kategori silinemedi' : 'Failed to delete category'));
    }
  };

  const handleReorder = async (categoryId, direction) => {
    try {
      await vehicleCategoriesAPI.reorder(categoryId, direction);
      fetchCategories();
    } catch (err) {
      toast.error(language === 'tr' ? 'Sıralama değiştirilemedi' : 'Failed to reorder');
    }
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setFormData({ id: category.id, new_id: category.id, name: category.name, name_tr: category.name_tr || '' });
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({ id: '', new_id: '', name: '', name_tr: '' });
  };

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {language === 'tr' ? 'Araç Kategorileri' : 'Vehicle Categories'}
          </h1>
          <p className="text-gray-400 mt-1">
            {language === 'tr' 
              ? 'Araç kategorilerini yönetin. Bu kategoriler menü ve galeri filtrelerinde görünür.' 
              : 'Manage vehicle categories. These appear in the menu and gallery filters.'}
          </p>
        </div>
        {!isCreating && !editingId && (
          <button
            onClick={() => { setIsCreating(true); setFormData({ id: '', new_id: '', name: '', name_tr: '' }); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)',
              color: '#0F172A'
            }}
          >
            <Plus size={20} />
            {language === 'tr' ? 'Yeni Kategori' : 'New Category'}
          </button>
        )}
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="mb-6 p-6 rounded-xl" style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid rgba(0, 240, 255, 0.2)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">
            {language === 'tr' ? 'Yeni Kategori Oluştur' : 'Create New Category'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                {language === 'tr' ? 'İsim (EN)' : 'Name (EN)'} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  setFormData({ 
                    ...formData, 
                    name: newName,
                    id: generateSlug(newName)
                  });
                }}
                placeholder="Category Name"
                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                {language === 'tr' ? 'İsim (TR)' : 'Name (TR)'}
              </label>
              <input
                type="text"
                value={formData.name_tr}
                onChange={(e) => setFormData({ ...formData, name_tr: e.target.value })}
                placeholder="Kategori İsmi"
                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                ID ({language === 'tr' ? 'otomatik oluşturulur' : 'auto-generated'})
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: generateSlug(e.target.value) })}
                placeholder="category-id"
                className="w-full px-4 py-2 rounded-lg bg-slate-700 border border-slate-600 text-gray-300 focus:border-cyan-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-cyan-500 text-slate-900 hover:bg-cyan-400 transition-all"
            >
              <Save size={18} />
              {language === 'tr' ? 'Kaydet' : 'Save'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-4 py-2 rounded-lg font-medium text-gray-400 hover:text-white transition-all"
            >
              {language === 'tr' ? 'İptal' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-3">
        {categories.map((category, index) => (
          <div 
            key={category.id}
            className="p-4 rounded-xl flex items-center justify-between"
            style={{ 
              background: 'rgba(30, 41, 59, 0.6)', 
              border: category.id === 'all' ? '1px solid rgba(255, 200, 0, 0.3)' : '1px solid rgba(0, 240, 255, 0.2)' 
            }}
          >
            {editingId === category.id ? (
              /* Edit Mode */
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{language === 'tr' ? 'İsim (EN)' : 'Name (EN)'}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setFormData({ 
                        ...formData, 
                        name: newName,
                        new_id: generateSlug(newName)
                      });
                    }}
                    className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{language === 'tr' ? 'İsim (TR)' : 'Name (TR)'}</label>
                  <input
                    type="text"
                    value={formData.name_tr}
                    onChange={(e) => setFormData({ ...formData, name_tr: e.target.value })}
                    className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{language === 'tr' ? 'Yeni ID' : 'New ID'}</label>
                  <input
                    type="text"
                    value={formData.new_id}
                    onChange={(e) => setFormData({ ...formData, new_id: generateSlug(e.target.value) })}
                    className="w-full px-3 py-2 rounded bg-slate-700 border border-slate-600 text-gray-300 focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{language === 'tr' ? 'Eski ID' : 'Old ID'}</label>
                  <input
                    type="text"
                    value={category.id}
                    disabled
                    className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0, 240, 255, 0.1)' }}>
                  <Plane size={20} className="text-cyan-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white">
                      {language === 'tr' && category.name_tr ? category.name_tr : category.name}
                    </span>
                    {category.id === 'all' && (
                      <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400">
                        {language === 'tr' ? 'Varsayılan' : 'Default'}
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-gray-400">ID: {category.id}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              {editingId === category.id ? (
                <>
                  <button
                    onClick={() => handleUpdate(category.id)}
                    className="p-2 rounded-lg bg-cyan-500 text-slate-900 hover:bg-cyan-400 transition-all"
                  >
                    <Save size={18} />
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="p-2 rounded-lg text-gray-400 hover:text-white transition-all"
                  >
                    {language === 'tr' ? 'İptal' : 'Cancel'}
                  </button>
                </>
              ) : (
                <>
                  {/* Reorder buttons */}
                  {category.id !== 'all' && (
                    <>
                      <button
                        onClick={() => handleReorder(category.id, 'up')}
                        disabled={index === 1} // Skip 'all' which is at index 0
                        className={`p-2 rounded-lg transition-all ${
                          index === 1 
                            ? 'text-gray-600 cursor-not-allowed' 
                            : 'text-gray-400 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button
                        onClick={() => handleReorder(category.id, 'down')}
                        disabled={index === categories.length - 1}
                        className={`p-2 rounded-lg transition-all ${
                          index === categories.length - 1 
                            ? 'text-gray-600 cursor-not-allowed' 
                            : 'text-gray-400 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <ChevronDown size={18} />
                      </button>
                    </>
                  )}

                  {category.id !== 'all' && (
                    <>
                      <button
                        onClick={() => startEdit(category)}
                        className="p-2 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-slate-700 transition-all"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-slate-700 transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Note */}
      <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(0, 240, 255, 0.1)', border: '1px solid rgba(0, 240, 255, 0.2)' }}>
        <p className="text-sm text-cyan-300">
          💡 {language === 'tr' 
            ? 'Not: Kategoriler navbar dropdown menüsünde, footer\'da ve araç galerisi filtrelerinde otomatik olarak görünür.' 
            : 'Note: Categories automatically appear in navbar dropdown, footer, and vehicle gallery filters.'}
        </p>
      </div>
    </div>
  );
};

export default AdminVehicleCategories;
